import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Home, 
  Calculator, 
  Phone, 
  BookOpen,
  MapPin,
  ChevronRight
} from 'lucide-react';

const Sitemap: React.FC = () => {
  const sitemapSections = [
    {
      title: "Main Pages",
      icon: <Home className="h-6 w-6" />,
      links: [
        { name: "Home", path: "/", description: "Welcome to Jara Safety Nets" },
        { name: "All Products", path: "/products", description: "Complete range of safety net products" },
        { name: "About Us", path: "/about", description: "Learn about our company and mission" },
        { name: "Contact", path: "/contact", description: "Get in touch with our team" },
        { name: "FAQ", path: "/faq", description: "Frequently asked questions" },
        { name: "Safety Consultation", path: "/landing", description: "Get free safety consultation" },
      ]
    },
    {
      title: "Safety Net Services",
      icon: <Calculator className="h-6 w-6" />,
      links: [
        { name: "Construction Safety Nets", path: "/construction-safety", description: "Fall protection for construction sites" },
        { name: "Sports Safety Nets", path: "/sports-safety", description: "Cricket, football, tennis court nets" },
        { name: "Industrial Safety Nets", path: "/industrial-safety", description: "Factory and industrial facility protection" },
        { name: "Pigeon Nets", path: "/pigeon-nets", description: "Anti-bird nets for buildings" },
        { name: "Child Safety Nets", path: "/child-safety-net", description: "Protective nets for children's safety" },
        { name: "Invisible Grills", path: "/invisible-grill", description: "Transparent safety grills" },
      ]
    },
    {
      title: "Specialized Safety Solutions",
      icon: <MapPin className="h-6 w-6" />,
      links: [
        { name: "Car Parking Nets", path: "/car-parking-net", description: "Vehicle protection from falling objects" },
        { name: "Swimming Pool Nets", path: "/swimming-pool-net", description: "Pool safety and debris protection" },
        { name: "Coconut Nets", path: "/coconut-net", description: "Coconut tree and fruit protection" },
        { name: "Construction Nets", path: "/construction-net", description: "Heavy-duty construction site nets" },
        { name: "Shade Nets", path: "/shade-net", description: "Agricultural and commercial shade nets" },
        { name: "General Safety Nets", path: "/safety-net", description: "Multi-purpose safety net solutions" },
      ]
    },
    {
      title: "Blog & Resources",
      icon: <BookOpen className="h-6 w-6" />,
      links: [
        { 
          name: "Blog", 
          path: "/blog", 
          description: "Safety net insights and installation guides" 
        },
        { 
          name: "Safety Net Installation Guide", 
          path: "/blog/safety-net-installation-guide", 
          description: "Complete guide to safety net installation" 
        },
        { 
          name: "Construction Safety Standards", 
          path: "/blog/construction-safety-standards", 
          description: "OSHA compliance and safety regulations" 
        },
      ]
    }
  ];

  return (
    <div className="pt-16">
      {/* Hero Section */}
      <section className="relative text-white py-20 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" style={{
          backgroundImage: `url('/images/building-nets.webp')`
        }}></div>
        
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-gray-900/70"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl lg:text-5xl font-bold mb-6"
          >
            Website Sitemap
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl text-white max-w-3xl mx-auto font-medium"
          >
            Explore all pages and resources on Jara Safety Nets website
          </motion.p>
        </div>
      </section>

      {/* Sitemap Content */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {sitemapSections.map((section, sectionIndex) => (
            <motion.div
              key={sectionIndex}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: sectionIndex * 0.1 }}
              className="mb-12"
            >
              {/* Section Header */}
              <div className="flex items-center space-x-3 mb-6">
                <div className="text-yellow-500">
                  {section.icon}
                </div>
                <h2 className="text-2xl lg:text-3xl font-bold text-gray-900">
                  {section.title}
                </h2>
              </div>

              {/* Links Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {section.links.map((link, linkIndex) => (
                  <Link
                    key={linkIndex}
                    to={link.path}
                    className="group bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 border border-gray-200 hover:border-yellow-500"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <ChevronRight className="h-4 w-4 text-yellow-500 group-hover:translate-x-1 transition-transform" />
                          <h3 className="font-semibold text-gray-900 group-hover:text-yellow-600 transition-colors">
                            {link.name}
                          </h3>
                        </div>
                        <p className="text-sm text-gray-600 ml-6">
                          {link.description}
                        </p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </motion.div>
          ))}

          {/* Contact CTA */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mt-12 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-2xl p-8 text-center text-white"
          >
            <h3 className="text-2xl lg:text-3xl font-bold mb-4">
              Can't Find What You're Looking For?
            </h3>
            <p className="text-lg mb-6 max-w-2xl mx-auto">
              Contact our team for personalized assistance with your safety net needs
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-white text-gray-900 hover:bg-gray-100 px-8 py-3 rounded-lg font-semibold transition-colors inline-flex items-center justify-center"
              >
                <Phone className="mr-2 h-5 w-5" />
                Contact Us
              </Link>
              <Link
                to="/landing"
                className="border-2 border-white text-white hover:bg-white hover:text-gray-900 px-8 py-3 rounded-lg font-semibold transition-colors inline-flex items-center justify-center"
              >
                <Calculator className="mr-2 h-5 w-5" />
                Safety Consultation
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Sitemap;

